import JSZip from 'jszip';
import storage from './storage';

const getAssetTypeForDataFormat = dataFormat => {
    const normalized = String(dataFormat || '').toLowerCase();
    if (normalized === 'svg') {
        return storage.AssetType.ImageVector;
    }
    if (['png', 'bmp', 'jpg', 'jpeg', 'gif', 'webp'].includes(normalized)) {
        return storage.AssetType.ImageBitmap;
    }
    if (['wav', 'mp3'].includes(normalized)) {
        return storage.AssetType.Sound;
    }
    return null;
};

const fetchAssetPayload = async md5ext => {
    const [assetId, dataFormat] = String(md5ext).split('.');
    const assetType = getAssetTypeForDataFormat(dataFormat);
    if (!assetId || !dataFormat || !assetType) {
        throw new Error(`Unsupported asset reference: ${md5ext}`);
    }
    const response = await fetch(storage.getAssetGetConfig({
        assetId,
        dataFormat
    }), {
        cache: 'no-store',
        credentials: 'same-origin'
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch asset ${md5ext}: ${response.status}`);
    }
    const buffer = await response.arrayBuffer();
    return {
        assetId,
        assetType,
        dataFormat,
        data: Array.from(new Uint8Array(buffer))
    };
};

const createRuntimeAsset = async md5ext => {
    const payload = await fetchAssetPayload(md5ext);
    return storage.createAsset(
        payload.assetType,
        payload.dataFormat,
        new Uint8Array(payload.data),
        payload.assetId,
        false
    );
};

const createLibrarySound = async sound => {
    const asset = await createRuntimeAsset(sound.md5ext);
    return {
        name: sound.name,
        md5: `${asset.assetId}.${asset.dataFormat}`,
        dataFormat: asset.dataFormat,
        format: sound.dataFormat || asset.dataFormat,
        rate: sound.rate,
        sampleCount: sound.sampleCount,
        asset,
        assetId: asset.assetId
    };
};

const stripLibraryAssetPayload = item => {
    const nextItem = {...item};
    delete nextItem.asset;
    return nextItem;
};

const hydrateLibraryAsset = async item => ({
    ...item,
    asset: await fetchAssetPayload(item.md5ext)
});

const hydrateLibrarySprite = async sprite => ({
    ...sprite,
    costumes: await Promise.all((sprite.costumes || []).map(hydrateLibraryAsset)),
    sounds: await Promise.all((sprite.sounds || []).map(hydrateLibraryAsset))
});

const createLibrarySpriteArchive = async sprite => {
    const zip = new JSZip();
    const hydratedSprite = await hydrateLibrarySprite(sprite);

    const spriteJson = {
        ...hydratedSprite,
        costumes: (hydratedSprite.costumes || []).map(stripLibraryAssetPayload),
        sounds: (hydratedSprite.sounds || []).map(stripLibraryAssetPayload)
    };

    zip.file('sprite.json', JSON.stringify(spriteJson));

    (hydratedSprite.costumes || []).forEach(costume => {
        if (costume.asset && costume.md5ext) {
            zip.file(costume.md5ext, new Uint8Array(costume.asset.data));
        }
    });
    (hydratedSprite.sounds || []).forEach(sound => {
        if (sound.asset && sound.md5ext) {
            zip.file(sound.md5ext, new Uint8Array(sound.asset.data));
        }
    });

    return zip.generateAsync({type: 'uint8array'});
};

export {
    createLibrarySpriteArchive,
    createLibrarySound,
    createRuntimeAsset,
    fetchAssetPayload,
    hydrateLibraryAsset,
    hydrateLibrarySprite
};
